import axios, { type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, AUTH_PERSIST_STORAGE_KEY } from '../store/auth.store';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1';

/**
 * GET liste publique `/shops` : ne pas envoyer de Bearer
 * (un JWT expiré ne doit pas faire échouer l’accueil).
 */
function isPublicCatalogGet(config: InternalAxiosRequestConfig): boolean {
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get') return false;
  const rawUrl = config.url ?? '';
  const [pathOnly, queryString = ''] = rawUrl.split('?');
  const params = new URLSearchParams(queryString);

  // Dashboard commerçant : includeInactive exige JWT (sinon 401 → catalogue vide)
  if (params.get('includeInactive') === 'true') return false;

  // Liste publique uniquement : un JWT expiré ne doit pas faire échouer l'accueil.
  // Fiche boutique / produits : garder le Bearer pour l'aperçu commerçant/admin
  // d'une vitrine pas encore validée (optionalAuth côté API).
  if (pathOnly === '/shops') return true;

  return false;
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

function unsetJsonContentType(config: InternalAxiosRequestConfig): void {
  if (typeof FormData === 'undefined' || !(config.data instanceof FormData)) return;
  const headers = config.headers;
  if (!headers) return;
  if (typeof headers.delete === 'function') headers.delete('Content-Type');
  else delete (headers as { 'Content-Type'?: string })['Content-Type'];
}

// Intercepteur requête : token JWT sauf lecture catalogue public
api.interceptors.request.use((config) => {
  // FormData : laisser le navigateur poser le boundary (sinon multer ne voit pas le fichier).
  unsetJsonContentType(config);

  if (isPublicCatalogGet(config)) {
    delete config.headers.Authorization;
  } else {
    const token = useAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;
  }

  const lang = useAuthStore.getState().user?.language ?? 'fr';
  config.headers['Accept-Language'] = lang;

  return config;
});

// Intercepteur réponse : rafraîchir le token si expiré
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        const refreshUrl = import.meta.env.VITE_API_URL
          ? `${import.meta.env.VITE_API_URL}/api/v1/auth/refresh`
          : '/api/v1/auth/refresh';

        const { data } = await axios.post(
          refreshUrl,
          refreshToken ? { refreshToken } : {},
          { withCredentials: true },
        );
        useAuthStore.getState().setAccessToken(data.accessToken);
        useAuthStore.setState({ refreshToken: null });
        original.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(original);
      } catch {
        useAuthStore.getState().logout();
        if (typeof window === 'undefined') return Promise.reject(error);
        const p = window.location.pathname;
        if (p === '/login' || p === '/register') return Promise.reject(error);
        const isPublicBrowsing =
          p === '/' ||
          p.startsWith('/shop/') ||
          p.startsWith('/client') ||
          p === '/landing' ||
          p.startsWith('/dev/');
        if (isPublicBrowsing) {
          try {
            localStorage.removeItem(AUTH_PERSIST_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          window.location.replace('/');
        } else {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);
